// frontend/src/components/ReportsPage.tsx
// Версия: 2.1.0 (2026-01-19) - Добавлен фильтр периода и кнопка "ТЕКУЩЕЕ ДЕЖУРСТВО"
// Изменения v2.1.0:
//   - Добавлена панель фильтров с кнопками (ТЕКУЩЕЕ ДЕЖУРСТВО, ОТЧЕТ ЗА ПЕРИОД, ПЕЧАТЬ СПИСКА, СТАТИСТИКА)
//   - Добавлены поля периода "С" и "По" (datetime-local)
//   - Фильтрация отчётов по дате создания/отправки
// Изменения v2.0.1: Колонка "Дата создания" теперь показывает sent_at (дата отправки) или created_at как fallback

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/paths';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  Button,
  Chip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  CalendarMonth as CalendarIcon,
  Description as DescriptionIcon,
  Print as PrintIcon,
  BarChart as BarChartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { api } from '../api/client';
import ReportDetailsDialog from './ReportDetailsDialog';

const statusTranslations: Record<string, string> = {
  rejected: 'ОТКЛОНЕН',
  sent: 'ОТПРАВЛЕН',
  draft: 'ЧЕРНОВИК',
  ready: 'ГОТОВ',
  pending: 'ОЖИДАЕТ',
  failed: 'ОШИБКА',
};

const reportTypeTranslations: Record<string, string> = {
  REJECTION: 'Отклонение',
  TEST_CONFIRMATION: 'Подтверждение теста',
  ANNUAL_REPORT: 'Годовой отчёт',
  INCIDENT_REPORT: 'Отчёт об инциденте',
};

// Маппинг типа документа по статусу (report_type не приходит из API confirmation_documents)
const getDocumentTypeLabel = (report: {
  report_type?: string;
  status?: string;
  document_number?: string;
}): string => {
  // Если report_type задан и есть в словаре — используем его
  if (report.report_type && reportTypeTranslations[report.report_type]) {
    return reportTypeTranslations[report.report_type];
  }
  // Определяем по document_number
  if (report.document_number?.includes('REJECT')) return 'Отклонение заявки';
  // Определяем по статусу документа
  if (report.status === 'rejected') return 'Отклонение заявки';
  if (report.status === 'sent') return 'Подтверждение теста';
  if (report.status === 'ready') return 'Подтверждение (готово)';
  if (report.status === 'draft') return 'Подтверждение (черновик)';
  if (report.status === 'pending') return 'Подтверждение (ожидает)';
  if (report.status === 'failed') return 'Подтверждение (ошибка)';
  return 'Подтверждение';
};

const formatDate = (dateString: string): string => {
  if (!dateString || dateString === 'Invalid Date') return 'Н/Д';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Неверная дата';
  return (
    date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow',
    }) + ' МСК'
  );
};

// Функция расчёта периода текущего дежурства (08:00 - 08:00 следующего дня)
const getCurrentDutyPeriod = (): { start: Date; end: Date } => {
  const now = new Date();
  const currentHour = now.getHours();

  let dutyStart: Date;
  let dutyEnd: Date;

  if (currentHour >= 8) {
    // После 08:00 - дежурство началось сегодня
    dutyStart = new Date(now);
    dutyStart.setHours(8, 0, 0, 0);
    dutyEnd = new Date(dutyStart);
    dutyEnd.setDate(dutyEnd.getDate() + 1);
  } else {
    // До 08:00 - дежурство началось вчера
    dutyStart = new Date(now);
    dutyStart.setDate(dutyStart.getDate() - 1);
    dutyStart.setHours(8, 0, 0, 0);
    dutyEnd = new Date(now);
    dutyEnd.setHours(8, 0, 0, 0);
  }

  return { start: dutyStart, end: dutyEnd };
};

// Форматирование даты для input datetime-local
const formatDateForInput = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

interface Report {
  id: number;
  document_number?: string;
  vessel_name: string;
  mmsi: string;
  ssas_number: string;
  imo_number: string;
  owner_name?: string;
  report_type: string;
  created_at: string;
  status: string;
  sent_at?: string;
  sent_by?: string;
  operator_name?: string;
  recipient_email?: string;
}

interface ConfirmationsResponse {
  success: boolean;
  total: number;
  data: Report[];
}

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [filteredReports, setFilteredReports] = useState<Report[]>([]);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Фильтры периода
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [currentDutyActive, setCurrentDutyActive] = useState(false);

  // Загрузка отчётов
  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);

      const queryString = params.toString();
      const url = `/confirmations${queryString ? `?${queryString}` : ''}`;

      const response = await api.get<ConfirmationsResponse>(url);
      setReports(response.data || []);
    } catch (err) {
      console.error('Ошибка загрузки отчётов:', err);
      setError('Не удалось загрузить отчёты');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Фильтрация по периоду (клиентская)
  useEffect(() => {
    let filtered = [...reports];

    if (dateFrom || dateTo) {
      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo) : null;

      filtered = filtered.filter((report) => {
        const reportDate = new Date(report.sent_at || report.created_at);
        if (fromDate && reportDate < fromDate) return false;
        if (toDate && reportDate > toDate) return false;
        return true;
      });
    }

    setFilteredReports(filtered);
  }, [reports, dateFrom, dateTo]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredReports.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredReports.length, page, rowsPerPage]);

  const paginatedReports = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredReports.slice(start, start + rowsPerPage);
  }, [filteredReports, page, rowsPerPage]);

  // Обработчик кнопки "ТЕКУЩЕЕ ДЕЖУРСТВО"
  const handleCurrentDuty = () => {
    const { start, end } = getCurrentDutyPeriod();
    setDateFrom(formatDateForInput(start));
    setDateTo(formatDateForInput(end));
    setCurrentDutyActive(true);
    setPage(0);
  };

  // Сброс фильтра периода
  const handleClearPeriod = () => {
    setDateFrom('');
    setDateTo('');
    setCurrentDutyActive(false);
    setPage(0);
  };

  const handleRowClick = (report: Report) => {
    setSelectedReport(report);
  };

  const handleDownload = async (id: number) => {
    try {
      window.open(apiUrl(`confirmations/${id}/pdf`), '_blank');
    } catch (err) {
      console.error('Ошибка скачивания PDF:', err);
    }
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  // Обработчик изменения дат (сброс флага "Текущее дежурство" при ручном изменении)
  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFrom(e.target.value);
    setCurrentDutyActive(false);
    setPage(0);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTo(e.target.value);
    setCurrentDutyActive(false);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Форматирование периода дежурства для отображения
  const getDutyPeriodLabel = (): string => {
    if (!currentDutyActive || !dateFrom || !dateTo) return '';
    const start = new Date(dateFrom);
    const end = new Date(dateTo);
    return `${start.toLocaleDateString('ru-RU')} 08:00 — ${end.toLocaleDateString('ru-RU')} 08:00`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Отчёты и документы
        {currentDutyActive && (
          <Chip
            label={`Дежурство: ${getDutyPeriodLabel()}`}
            color="primary"
            size="small"
            sx={{ ml: 2, verticalAlign: 'middle' }}
            onDelete={handleClearPeriod}
          />
        )}
      </Typography>

      {/* Панель кнопок фильтров */}
      <Box
        data-print="hide"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          variant={currentDutyActive ? 'contained' : 'outlined'}
          color="primary"
          startIcon={<CalendarIcon />}
          onClick={handleCurrentDuty}
          size="small"
        >
          ТЕКУЩЕЕ ДЕЖУРСТВО
        </Button>

        <Button
          variant="outlined"
          color="primary"
          startIcon={<AssessmentIcon />}
          onClick={() => {
            const { start, end } = getCurrentDutyPeriod();
            const prefix = window.location.pathname.startsWith('/admin') ? '/admin' : '/operator';
            navigate(`${prefix}/duty-report?from=${start.toISOString()}&to=${end.toISOString()}`);
          }}
          size="small"
        >
          ОТЧЁТ ЗА СМЕНУ
        </Button>

        <Button
          variant="outlined"
          color="primary"
          startIcon={<DescriptionIcon />}
          onClick={() => {
            /* TODO: Отчёт за период */
          }}
          size="small"
        >
          ОТЧЕТ ЗА ПЕРИОД
        </Button>

        <Button
          variant="outlined"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          size="small"
        >
          ПЕЧАТЬ СПИСКА
        </Button>

        <Button
          variant="outlined"
          color="primary"
          startIcon={<BarChartIcon />}
          onClick={() => {
            /* TODO: Статистика */
          }}
          size="small"
        >
          СТАТИСТИКА
        </Button>
      </Box>

      {/* Поля периода */}
      <Box
        data-print="hide"
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}
      >
        <TextField
          label="С"
          type="datetime-local"
          value={dateFrom}
          onChange={handleDateFromChange}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 200 }}
        />

        <TextField
          label="По"
          type="datetime-local"
          value={dateTo}
          onChange={handleDateToChange}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 200 }}
        />

        {(dateFrom || dateTo) && (
          <Typography variant="body2" color="textSecondary">
            Показано: {filteredReports.length} из {reports.length}
          </Typography>
        )}
      </Box>

      {/* Поиск и фильтр статуса */}
      <Box data-print="hide" sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Поиск по судну, MMSI, IMO, терминалу..."
          variant="outlined"
          value={search}
          onChange={handleSearchChange}
          sx={{ flexGrow: 1 }}
          size="small"
        />
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>Статус</InputLabel>
          <Select value={statusFilter} onChange={handleStatusFilterChange} label="Статус">
            <MenuItem value="">Все</MenuItem>
            <MenuItem value="rejected">ОТКЛОНЕН</MenuItem>
            <MenuItem value="sent">ОТПРАВЛЕН</MenuItem>
            <MenuItem value="draft">ЧЕРНОВИК</MenuItem>
            <MenuItem value="ready">ГОТОВ</MenuItem>
            <MenuItem value="pending">ОЖИДАЕТ</MenuItem>
            <MenuItem value="failed">ОШИБКА</MenuItem>
            <MenuItem value="expired">ПРОСРОЧЕНА</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Только для печати: счётчик записей */}
      <Box
        sx={{
          display: 'none',
          '@media print': { display: 'block', mt: 1, mb: 1, fontStyle: 'italic', fontSize: '10pt' },
        }}
      >
        Показано {filteredReports.length} из {reports.length} отчётов
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>№ Документа</TableCell>
              <TableCell>Тип отчёта</TableCell>
              <TableCell>Судно</TableCell>
              <TableCell>MMSI</TableCell>
              <TableCell>IMO</TableCell>
              <TableCell>Номер терминала</TableCell>
              <TableCell>Судовладелец</TableCell>
              <TableCell>Дата создания</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Кем отправлен</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography color="textSecondary">Загрузка...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredReports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography color="textSecondary">Нет данных для отображения</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedReports.map((report) => (
                <TableRow
                  key={report.id}
                  onClick={() => handleRowClick(report)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <TableCell>{report.document_number || '—'}</TableCell>
                  <TableCell>{getDocumentTypeLabel(report)}</TableCell>
                  <TableCell>{report.vessel_name || '—'}</TableCell>
                  <TableCell>{report.mmsi || '—'}</TableCell>
                  <TableCell>{report.imo_number || '—'}</TableCell>
                  <TableCell>{report.ssas_number || '—'}</TableCell>
                  <TableCell>{report.owner_name || '—'}</TableCell>
                  {/* ИСПРАВЛЕНО: Используем sent_at (дата отправки) или created_at как fallback */}
                  <TableCell>{formatDate(report.sent_at || report.created_at)}</TableCell>
                  <TableCell>{statusTranslations[report.status] || report.status}</TableCell>
                  <TableCell>
                    {report.operator_name
                      ? report.operator_name
                      : report.sent_by === 'AUTO'
                        ? 'Автоматически'
                        : report.sent_by === 'BATCH'
                          ? 'Пакетная'
                          : report.sent_by || '—'}
                  </TableCell>
                  <TableCell>
                    {report.status !== 'rejected' && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(report.id);
                        }}
                        title="Скачать PDF"
                        size="small"
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredReports.length > 0 && (
        <TablePagination
          data-print="hide"
          component="div"
          count={filteredReports.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Строк на странице"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} из ${count !== -1 ? count : `больше ${to}`}`
          }
        />
      )}

      {selectedReport && (
        <ReportDetailsDialog
          report={selectedReport}
          open={!!selectedReport}
          onClose={() => setSelectedReport(null)}
          onDownload={handleDownload}
        />
      )}
    </Box>
  );
};

export default ReportsPage;
